import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { communitySearchResultsAfterUpdate, CommunityOverview, CommunityPerson, CommunitySettingsRequest, EventCommunity, EventInterest } from '@radevents/shared';
import service from '../../services/communityService';
import { CommunityView, EventInterestView } from './CommunityParts';
import PublicHeader from '../Layout/PublicHeader';
import AppTopbarBrand from '../Layout/AppTopbarBrand';
import { useAuth } from '../../contexts/AuthContext';
import { clearListCache } from '../../hooks/useListCache';

const errorText = (e: any) => e?.response?.data?.error || 'Community konnte nicht geladen werden. Bitte versuche es erneut.';
export default function Community() {
  const { user } = useAuth();
  return <CommunityAccount key={user?.id} />;
}
export function CommunityContent({ onChanged }: { onChanged?: () => void }) {
  const { user } = useAuth();
  return <CommunityAccount key={user?.id} embedded onChanged={onChanged} />;
}
/**
 * Freunde laden und ändern, für die Verwaltung und die Freundesleiste in Meine Events
 * (30.10.2026): beide lesen dieselben Daten, statt sie zweimal zu holen.
 */
export function useCommunity(onChanged?: () => void) {
  const [data, setData] = useState<CommunityOverview | null>(null);
  const [results, setResults] = useState<CommunityPerson[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const load = useCallback(() => {
    setLoading(true); setError('');
    service.overview().then(setData).catch(e => setError(errorText(e))).finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);
  const run = async (work: () => Promise<void>) => {
    setBusy(true); setError('');
    try { await work(); onChanged?.(); clearListCache('landing'); clearListCache('favorites'); }
    catch (e) { setError(errorText(e)); }
    finally { setBusy(false); }
  };
  const save = (p: CommunitySettingsRequest) => { void run(async () => { setData(await service.saveSettings(p)); }); };
  const renewInvite = () => { void run(async () => { setData(await service.renewInvite()); }); };
  const search = (q: string) => { setResults(null); void run(async () => { setResults(await service.search(q)); }); };
  const action = (p: CommunityPerson, a: 'request' | 'accept' | 'remove' | 'block') => { void run(async () => { const next = await service.act(p.id, a); setData(next); setResults(list => communitySearchResultsAfterUpdate(list, next, p.id)); }); };
  return { data, results, loading, busy, error, load, save, renewInvite, search, action };
}
/**
 * Die Verwaltung (Suche, Einladung, Listen, Auffindbarkeit) zu einem `useCommunity`-Stand, unter
 * dem `FreundeKopf` in Meine Events. Offene Anfragen stehen dort schon; die Mail zur Anfrage
 * öffnet beides (`?verwalten=1`), sonst stünde dieselbe Anfrage zweimal untereinander.
 */
export function CommunityManager({ c, startAdding }: { c: ReturnType<typeof useCommunity>; startAdding?: boolean }) {
  return <CommunityView startAdding={startAdding} ohneAnfragen key={c.data ? 'loaded' : 'loading'} data={c.data} results={c.results} loading={c.loading} busy={c.busy} error={c.error} onRetry={c.load} onSave={c.save} onSearch={c.search} onAction={c.action} onRenewInvite={c.renewInvite} />;
}
function CommunityAccount({ embedded = false, onChanged }: { embedded?: boolean; onChanged?: () => void }) {
  const navigate = useNavigate();
  const c = useCommunity(onChanged);
  const { data, results, loading, busy, error, load, save, renewInvite, search, action } = c;
  if (embedded) return <CommunityView key={data ? 'loaded' : 'loading'} data={data} results={results} loading={loading} busy={busy} error={error} onRetry={load} onSave={save} onSearch={search} onAction={action} onRenewInvite={renewInvite} />;
  return <div style={{ background: '#F5F3F3', minHeight: '100vh' }}>
    <div className="hide-mobile"><PublicHeader /></div>
    <div className="app-topbar hide-desktop"><AppTopbarBrand onBack={() => navigate('/profile')} /></div>
    <main className="community-page">
      <CommunityView key={data ? 'loaded' : 'loading'} data={data} results={results} loading={loading} busy={busy} error={error} onRetry={load} onSave={save} onSearch={search} onAction={action} onRenewInvite={renewInvite} />
      {data?.profile && <p style={{ marginTop: 20 }}><Link to="/favorites?view=friends">Events deiner Freunde entdecken</Link></p>}
    </main>
  </div>;
}
export function EventCommunityPanel({ eventId, compact = false, initialData, onChanged }: { eventId: number; compact?: boolean; initialData?: EventCommunity; onChanged?: (data: EventCommunity) => void }) {
  const [data, setData] = useState<EventCommunity | null>(initialData ?? null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (initialData) { setData(initialData); return; }
    let active = true; setData(null); setError('');
    service.event(eventId).then(d => { if (active) setData(d); }).catch(e => { if (active) setError(errorText(e)); });
    return () => { active = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);
  const save = async (interest: EventInterest | null) => {
    setBusy(true); setError('');
    try { const next = await service.saveInterest(eventId, interest); setData(next); onChanged?.(next); clearListCache('landing'); clearListCache('favorites'); }
    catch (e) { setError(errorText(e)); }
    finally { setBusy(false); }
  };
  return <div className={compact ? "event-participation-wrap event-participation-wrap--compact" : "event-participation-wrap"}>{data ? <EventInterestView data={data} compact={compact} busy={busy} error={error} onSave={save} /> : <p role={error ? 'alert' : 'status'}>{error || 'Eventplanung wird geladen …'}</p>}
  </div>;
}
